// Honest venue/location display logic for runs.
//
// Google Places `name` (stored as location_name) is sometimes a real venue
// name ("Life Time Northbrook") and sometimes just the street-address portion
// ("1885 Holste Rd"). This pure helper decides what to show without inventing
// names or inferring from title.
//
// Returns { primary, secondary }:
//   - primary:   the prominent line (venue name OR full address)
//   - secondary: the muted line below (full address) or null
//
// Collapse to a single full-address line ONLY when location_name looks
// address-like (starts with a street number) AND is redundant with the address
// after normalization. A real venue name that merely appears inside the address
// is still shown as venue + address.

const STREET_SUFFIX = {
  rd: 'road', road: 'road',
  st: 'street', street: 'street',
  ave: 'avenue', av: 'avenue', avenue: 'avenue',
  blvd: 'boulevard', boulevard: 'boulevard',
  dr: 'drive', drive: 'drive',
  ln: 'lane', lane: 'lane',
  ct: 'court', court: 'court',
  hwy: 'highway', highway: 'highway',
  pkwy: 'parkway', parkway: 'parkway',
  pl: 'place', place: 'place',
  ter: 'terrace', terrace: 'terrace',
  cir: 'circle', circle: 'circle',
};

// Lowercase, trim, strip punctuation, collapse spaces, expand street suffixes.
export function normalizeLocation(s) {
  if (!s) return '';
  let t = String(s).toLowerCase();
  t = t.replace(/[^\w\s]/g, ' '); // strip punctuation
  t = t.replace(/\s+/g, ' ').trim(); // collapse whitespace
  if (!t) return '';
  return t
    .split(' ')
    .map((w) => STREET_SUFFIX[w] || w)
    .join(' ');
}

// "1885 Holste Rd" -> true ; "Life Time Northbrook" -> false
export function looksAddressLike(name) {
  return /^\s*\d/.test(String(name || '').trim());
}

export function formatVenue(locationName, address) {
  const name = (locationName || '').trim();
  const addr = (address || '').trim();

  if (!addr) return { primary: name, secondary: null };
  if (!name) return { primary: addr, secondary: null };

  const nName = normalizeLocation(name);
  const nAddr = normalizeLocation(addr);

  // Collapse to a single full-address line only when the "name" is really an
  // address fragment that's redundant with the full address.
  const redundant = nAddr === nName || nAddr.startsWith(nName + ' ');
  if (looksAddressLike(name) && redundant) {
    return { primary: addr, secondary: null };
  }

  // Identical strings (non-address-like) -> one line, no duplicate.
  if (nName === nAddr) {
    return { primary: addr, secondary: null };
  }

  return { primary: name, secondary: addr };
}
